type WeatherStats = {
  temp?: number | null;
  humid?: number | null;
  solar?: number | null;
};

type MetricCardProps = {
  title: string;
  peakValue: number | null;
  averageValue: number | null;
  unit: string;
  accentColor: string;
  averageColor?: string;
  peakLabel?: string;
  averageLabel?: string;
  peakPrevDay?: number;
  averagePrevDay?: number;
  peakDate?: string;
  peakSlot?: number | null;
  peakWeather?: WeatherStats | null;
  avgWeather?: WeatherStats | null;
  showPeakWeather?: boolean;
};

function formatValue(value: number): string {
  if (Math.abs(value) >= 1000) {
    return value.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  }
  const s = value.toFixed(2);
  return s.endsWith(".00") ? String(Math.trunc(value)) : s.replace(/0$/, "");
}

function formatDate(iso: string) {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function fmtW(v: number | null | undefined): string {
  if (v == null) return "—";
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

const WeatherIcon = ({ type }: { type: "temp" | "humid" | "solar" }) => {
  if (type === "temp")
    return (
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
      </svg>
    );
  if (type === "humid")
    return (
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
      </svg>
    );
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
};

const WEATHER_ITEMS: {
  label: string;
  iconType: "temp" | "humid" | "solar";
  key: keyof WeatherStats;
  unit: string;
}[] = [
  { label: "Temperature", iconType: "temp", key: "temp", unit: "°C" },
  { label: "Humidity", iconType: "humid", key: "humid", unit: "%" },
  { label: "Solar Irrad.", iconType: "solar", key: "solar", unit: "W/m²" },
];

function WeatherRow({
  peakWeather,
  avgWeather,
  showPeakWeather = false,
  label = "Weather",
}: {
  peakWeather?: WeatherStats | null;
  avgWeather?: WeatherStats | null;
  showPeakWeather?: boolean;
  label?: string;
}) {
  return (
    <div className="pt-1">
      <div className="flex items-center gap-2 mb-2">
        <p className="text-[9px] font-medium text-[#8C8A96] uppercase tracking-wide whitespace-nowrap">
          {label}
        </p>
        <div className="flex-1 h-px bg-[#DFEAF2]" />
      </div>
      <div className="flex justify-between divide-x divide-[#DFEAF2]">
        {WEATHER_ITEMS.map((item, idx) => (
          <div
            key={item.key}
            className={`min-w-0 flex flex-col ${idx > 0 ? "pl-3" : ""}`}
          >
            <p className="text-[8.5px] text-[#8C8A96] mb-0.5 flex items-center gap-1">
              {item.label}
              <span
                style={{
                  color:
                    item.iconType === "temp"
                      ? "#f97316"
                      : item.iconType === "humid"
                        ? "#3b82f6"
                        : "#eab308",
                }}
              >
                <WeatherIcon type={item.iconType} />
              </span>
            </p>
            <p className="text-[11px] font-medium text-[#14171F] leading-tight">
              {fmtW(avgWeather?.[item.key])}{" "}
              <span className="text-[9px] font-normal text-[#6B7280]">
                {item.unit}
              </span>
            </p>
            {showPeakWeather &&
              (() => {
                const cur = peakWeather?.[item.key];
                const avg = avgWeather?.[item.key];
                if (cur == null || avg == null) return null;
                const diff = cur - avg;
                const color =
                  diff > 0 ? "#22c55e" : diff < 0 ? "#ef4444" : "#8C8A96";
                const arrow = diff > 0 ? "▲" : diff < 0 ? "▼" : "";
                return (
                  <p
                    className="text-[10px] font-medium leading-tight flex items-center gap-0.5"
                    style={{ color }}
                  >
                    {arrow && <span className="text-[8px]">{arrow}</span>}
                    {fmtW(cur)}
                  </p>
                );
              })()}
          </div>
        ))}
      </div>
    </div>
  );
}

export function MetricCard({
  title,
  peakValue,
  averageValue,
  unit,
  accentColor,
  peakLabel = "Peak",
  averageLabel = "Average",
  peakPrevDay,
  averagePrevDay,
  peakDate,
  peakSlot,
  peakWeather,
  avgWeather,
  showPeakWeather = false,
}: MetricCardProps) {
  const showPrevDay = peakPrevDay !== undefined || averagePrevDay !== undefined;

  const Col = ({
    label,
    value,
    prevDay,
    isPeak,
    align = "left",
  }: {
    label: string;
    value: number | null;
    prevDay?: number;
    isPeak?: boolean;
    align?: "left" | "right";
  }) => (
    <div className="min-w-0">
      <div className="flex items-center gap-1 mb-0.5 min-h-[18px]">
        <p className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wide whitespace-nowrap">
          {label}
        </p>
        {isPeak && peakDate && (
          <span className="text-[8px] font-semibold text-[#34656D] bg-[#E8F4F8] pl-1.5 py-0.5 rounded-md flex items-center gap-1 whitespace-nowrap">
            {formatDate(peakDate)}
            {peakSlot != null && (
              <span className="text-[8px] font-medium text-white bg-[#3D6B74] px-1 py-px rounded">
                {peakSlot}
              </span>
            )}
          </span>
        )}
      </div>
      <p className="text-lg font-medium text-[#14171F] leading-tight">
        {value == null ? (
          <span className="text-[#A3AED0]">—</span>
        ) : (
          <>
            {formatValue(value)}{" "}
            <span className="text-xs font-normal text-[#6B7280]">{unit}</span>
          </>
        )}
      </p>
      {showPrevDay &&
        prevDay !== undefined &&
        value != null &&
        (() => {
          const diff = prevDay - value;
          const color = diff > 0 ? "#22c55e" : diff < 0 ? "#ef4444" : "#8C8A96";
          const arrow = diff > 0 ? "▲" : diff < 0 ? "▼" : "";
          return (
            <p
              className="text-[10px] font-medium leading-tight flex items-center gap-0.5"
              style={{ color }}
            >
              {arrow && <span className="text-[8px]">{arrow}</span>}
              {formatValue(prevDay)}{" "}
              <span className="text-[9px] font-normal text-[#8C8A96]">
                prev day
              </span>
            </p>
          );
        })()}
    </div>
  );

  return (
    <div className="relative bg-white rounded-xl border border-[#DFEAF2] p-3 pl-5 flex flex-col gap-2">
      <div
        className="absolute left-0 top-[15%] bottom-[15%] w-[4px] rounded-full"
        style={{
          background: `linear-gradient(to bottom, ${accentColor}00, ${accentColor} 40%, ${accentColor} 60%, ${accentColor}00)`,
        }}
      />
      <p className="text-xs font-medium text-[#14171F]">{title}</p>
      <div className="flex items-start justify-between">
        <Col label={peakLabel} value={peakValue} prevDay={peakPrevDay} isPeak />
        <div className="w-px bg-[#DFEAF2] mx-3 self-stretch" />
        <Col
          label={averageLabel}
          value={averageValue}
          prevDay={averagePrevDay}
          align="right"
        />
      </div>
      <WeatherRow
        peakWeather={peakWeather}
        avgWeather={avgWeather}
        showPeakWeather={showPeakWeather}
        label={title === "Energy" ? "Average Weather" : "Weather"}
      />
    </div>
  );
}
